import z from "zod";
import { ReadingBand, ResultKind, Subject } from "./type.app.js";

export const API_SCHEMA_VERSION = 1;
export const AccessRole = z.enum(["owner", "contribute", "view"]);
export type AccessRole = z.infer<typeof AccessRole>;

export const StudentSummary = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  colorPairIndex: z.number().int().nonnegative(),
  readingBand: ReadingBand,
  createdAt: z.string(),
  subjects: z.record(
    Subject,
    z.object({
      level: z.number().int().min(1).max(100),
      boostLevel: z.number().int().min(1).max(100),
      correctAnswers: z.number().int().nonnegative(),
      answeredCount: z.number().int().nonnegative(),
      currentTextId: z.string().nullable(),
      levelEpoch: z.number().int().nonnegative().default(0),
      tracked: z.boolean().default(false),
    }),
  ),
  coins: z.number().int().nonnegative(),
  achievements: z.array(z.string()),
  inventory: z.array(z.string()),
  access: AccessRole,
});
export type StudentSummary = z.infer<typeof StudentSummary>;

export const SharePermission = z.enum(["view", "contribute"]);
export type SharePermission = z.infer<typeof SharePermission>;

export const ShareRecord = z.object({
  shareId: z.string().uuid(),
  studentId: z.string().uuid(),
  ownerId: z.string().min(1).optional(),
  email: z.string().email().optional(),
  ownerDisplayName: z.string().nullable().optional(),
  studentName: z.string().optional(),
  permission: SharePermission,
  status: z.enum(["pending", "accepted", "revoked", "declined", "expired"]),
  expiresAt: z.string().nullable().optional(),
  url: z.string().url().optional(),
});
export type ShareRecord = z.infer<typeof ShareRecord>;

export const BootstrapResponse = z.object({
  account: z.object({ id: z.string().uuid(), displayName: z.string().nullable() }),
  students: z.array(StudentSummary),
  shares: z.array(ShareRecord),
  serverTime: z.string(),
  schemaVersion: z.literal(API_SCHEMA_VERSION),
});
export type BootstrapResponse = z.infer<typeof BootstrapResponse>;

export const CreateShareResponse = z.object({
  shareId: z.string().uuid(),
  email: z.string().email(),
  permission: SharePermission,
  url: z.string().url(),
  expiresAt: z.string(),
});
export type CreateShareResponse = z.infer<typeof CreateShareResponse>;

export const SharePatchRequest = z.object({ permission: SharePermission });
export type SharePatchRequest = z.infer<typeof SharePatchRequest>;

export const ShareListResponse = z.object({ shares: z.array(ShareRecord) });
export type ShareListResponse = z.infer<typeof ShareListResponse>;

export const PublicShareInvitation = z.object({
  studentName: z.string().min(1).max(80),
  ownerDisplayName: z.string().nullable(),
  permission: SharePermission,
  expiresAt: z.string(),
  status: z.enum(["pending", "accepted", "revoked", "declined", "expired"]),
});
export type PublicShareInvitation = z.infer<typeof PublicShareInvitation>;

export const AcceptShareResponse = z.object({
  shareId: z.string().uuid(),
  ownerId: z.string().min(1),
  ownerDisplayName: z.string().nullable(),
  permission: SharePermission,
  student: StudentSummary,
});
export type AcceptShareResponse = z.infer<typeof AcceptShareResponse>;

export const StudentCreateRequest = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(80),
  colorPairIndex: z.number().int().nonnegative(),
  readingBand: ReadingBand,
  createdAt: z.string().datetime().optional(),
});
export type StudentCreateRequest = z.infer<typeof StudentCreateRequest>;

export const StudentPatchRequest = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    colorPairIndex: z.number().int().nonnegative().optional(),
    readingBand: ReadingBand.optional(),
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "At least one metadata field is required");
export type StudentPatchRequest = z.infer<typeof StudentPatchRequest>;

export const LevelAdjustmentRequest = z.object({
  subject: Subject,
  level: z.number().int().min(1).max(100),
  reason: z.string().trim().min(1).max(500),
});
export type LevelAdjustmentRequest = z.infer<typeof LevelAdjustmentRequest>;

export const StudentDetailResponse = z.object({
  student: StudentSummary,
  recentAnswers: z.array(
    z.object({
      eventId: z.string().uuid(),
      subject: Subject,
      itemId: z.string().nullable(),
      textId: z.string().nullable(),
      result: ResultKind,
      level: z.number().int(),
      answeredAt: z.string(),
    }),
  ),
});
export type StudentDetailResponse = z.infer<typeof StudentDetailResponse>;

export const ProfileViewResponse = z.object({
  detailVersion: z.number().int().nonnegative(),
  student: StudentSummary,
  subjectState: z.record(z.string(), z.record(z.string(), z.unknown())),
  itemPositions: z.array(z.object({ itemId: z.string(), x: z.number(), y: z.number() })),
});
export type ProfileViewResponse = z.infer<typeof ProfileViewResponse>;

export const AnswerPageResponse = z.object({
  answers: z.array(
    z.object({
      eventId: z.string().uuid(),
      subject: Subject,
      itemId: z.string().nullable(),
      textId: z.string().nullable(),
      result: ResultKind,
      level: z.number().int(),
      answeredAt: z.string(),
    }),
  ),
  nextCursor: z.string().nullable(),
});
export type AnswerPageResponse = z.infer<typeof AnswerPageResponse>;

const OperationBase = z.object({
  eventId: z.string().uuid(),
  studentId: z.string().uuid(),
  occurredAt: z.string(),
});

export const SyncOperation = z.discriminatedUnion("type", [
  OperationBase.extend({
    type: z.literal("student.created"),
    payload: z.object({
      name: z.string().min(1).max(80),
      colorPairIndex: z.number().int().nonnegative(),
      readingBand: ReadingBand,
    }),
  }),
  OperationBase.extend({
    type: z.literal("student.metadata.updated"),
    payload: z
      .object({
        name: z.string().min(1).max(80).optional(),
        colorPairIndex: z.number().int().nonnegative().optional(),
        readingBand: ReadingBand.optional(),
        archived: z.boolean().optional(),
      })
      .refine((value) => Object.keys(value).length > 0, "At least one metadata field is required"),
  }),
  OperationBase.extend({
    type: z.literal("subject.current.updated"),
    payload: z.object({
      subject: Subject,
      currentTextId: z.string().nullable(),
    }),
  }),
  OperationBase.extend({
    type: z.literal("answer.recorded"),
    payload: z.object({
      subject: Subject,
      itemId: z.string().max(200).optional(),
      textId: z.string().max(200).optional(),
      result: ResultKind,
      level: z.number().int().min(1).max(100),
      studentLevel: z.number().int().min(1).max(100).optional(),
      studentBoostLevel: z.number().int().min(1).max(100).optional(),
      levelEpoch: z.number().int().nonnegative().optional(),
    }),
  }),
  OperationBase.extend({
    type: z.literal("coins.granted"),
    payload: z.object({
      delta: z.number().int().positive(),
      reason: z.enum(["answer", "achievement", "bonus", "admin_grant"]),
      sourceEventId: z.string().uuid().optional(),
    }),
  }),
  OperationBase.extend({
    type: z.literal("achievement.achieved"),
    payload: z.object({ achievementId: z.string().min(1).max(200) }),
  }),
  OperationBase.extend({
    type: z.literal("purchase.completed"),
    payload: z.object({ itemId: z.string().min(1).max(200) }).strict(),
  }),
  OperationBase.extend({
    type: z.literal("item.position.updated"),
    payload: z.object({
      itemId: z.string().min(1).max(200),
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
    }),
  }),
  OperationBase.extend({
    type: z.literal("level.adjusted"),
    payload: z.object({
      subject: Subject,
      level: z.number().int().min(1).max(100),
      levelEpoch: z.number().int().nonnegative().optional(),
      reason: z.string().min(1).max(500),
    }),
  }),
]);
export type SyncOperation = z.infer<typeof SyncOperation>;

export const SyncBatchRequest = z.object({
  clientId: z.string().min(1).max(200),
  operations: z.array(SyncOperation).min(1).max(500),
});
export type SyncBatchRequest = z.infer<typeof SyncBatchRequest>;

export const SyncBatchResponse = z.object({
  serverTime: z.string(),
  accepted: z.array(z.string().uuid()),
  duplicates: z.array(z.string().uuid()),
  rejected: z.array(z.object({ eventId: z.string().uuid(), code: z.string(), message: z.string() })),
  students: z.array(z.object({ id: z.string().uuid(), updated: z.boolean(), summaryVersion: z.number().int() })),
});
export type SyncBatchResponse = z.infer<typeof SyncBatchResponse>;
